import { Injectable, NgZone, Signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { FeatureFlag } from '@app/enums';
import { HumiInquiryStatus } from '@app/models/kyc-kyb/types';
import { CompanyAdminContact } from '@app/models/payroll/company/company-admin-contact';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { KYCKYBService } from '@app/services/kyc-kyb/kyc-kyb.service';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { BehaviorSubject, combineLatest, from, Observable, of } from 'rxjs';
import { filter, map, switchMap, takeWhile } from 'rxjs/operators';
import { OnboardingStatusService } from '../self-serve/services/onboarding-status.service';
import { DisplayConditions, KYCKYBDisplayConditions, SelfServeDisplayConditions } from './types';

/**
 * SpecialInformationBarService loads data and sends out events regarding special information.
 * Special Information is information the user should care about, but does not related to a specific
 * section of the application.
 *
 * @see SpecialInformationBar
 */
@Injectable({
    providedIn: 'root',
})
export class SpecialInformationBarService {
    displayConditions$: Observable<DisplayConditions | undefined>;
    displayConditionsSignal: Signal<DisplayConditions | undefined>;

    private payrollAdminContactStatus$ = new BehaviorSubject<boolean>(true);

    get specialInformationBarHeight$(): Observable<number> {
        return this._specialInformationBarHeight$.asObservable();
    }

    private specialInformationBarHeightResizeObserver: ResizeObserver;

    private _specialInformationBarHeight$: BehaviorSubject<number> = new BehaviorSubject(0);

    constructor(
        private ngZone: NgZone,
        private authService: AuthService,
        private onboardingStatusService: OnboardingStatusService,
        private knowYourService: KYCKYBService,
        private featureService: FeatureService,
        private router: Router,
        private translateService: TranslateService
    ) {
        // prepare the resize observer
        // at this point, it doesn't know what it will observe, but it'll be ready
        this.specialInformationBarHeightResizeObserver = new ResizeObserver((entries) =>
            this.ngZone.run(() =>
                entries.forEach((entry) => this._specialInformationBarHeight$.next(entry.contentRect.height))
            )
        );

        this.displayConditions$ = this.getDisplayConditions();
        this.displayConditionsSignal = toSignal(this.displayConditions$);
    }

    /**
     * observeSpecialInformationBar sets up the observation of the special information bar.
     * This element need to be supplied by the Angular component.
     */
    observeSpecialInformationBar(specialInformationBarNativeElement: HTMLElement): void {
        // remove any previous subscriptions (there should be none)
        this.specialInformationBarHeightResizeObserver.disconnect();

        this.specialInformationBarHeightResizeObserver.observe(specialInformationBarNativeElement);
    }

    private get isAdmin(): boolean {
        return this.authService.isAdmin();
    }

    private get hasPayroll(): boolean {
        return this.authService.hasPayrollAccess;
    }

    private get hasKYCExpired(): boolean {
        return this.authService.account.isPayrollAccessBlocked;
    }

    private get hasKYBExpired(): boolean {
        // Check if company's persona verification is expiring/expired (within 30 days)
        // Used for both KYC and KYB re-verification banners
        const expiresAt = this.authService.company.personaExpiresAt;
        if (!expiresAt) {
            return false;
        }

        const thirtyDaysBeforeExpiry = moment(expiresAt).subtract(30, 'days');
        return moment().isAfter(thirtyDaysBeforeExpiry);
    }

    private get isEmployeeOnboarding(): boolean {
        const employee = this.authService.employee;

        if (!employee) {
            return false;
        }

        return employee.isOnboarding();
    }

    /**
     * Retrieves the data required for displaying different special information bars.
     * Additional data can be added here if required for a new special info bar
     */
    private getDisplayConditions(): Observable<DisplayConditions | undefined> {
        return this.authService.onLoginStatusChange.asObservable().pipe(
            switchMap((isLoggedIn) => {
                // Only emit once the user is logged in, otherwise we don't have the data to determine whether the special info bars should display
                if (!isLoggedIn) {
                    this.payrollAdminContactStatus$.next(true);
                    return of(undefined);
                }

                // Trigger initial payroll contact check on login
                if (this.hasPayroll) {
                    this.refreshPayrollAdminContactStatus();
                }

                return combineLatest([
                    this.getCurrentURL(),
                    this.getOnboardingStatus(),
                    this.getKYCKYBStatus(),
                    this.getPayrollAdminContactStatus(),
                    this.getHasKYBExpiredStatus(),
                ]).pipe(
                    map(([currentURL, selfServe, KYCKYB, hasPayrollAdminContact, hasKYBExpired]) => {
                        return {
                            isAdmin: this.isAdmin,
                            hasPayroll: this.hasPayroll,
                            currentURL,
                            isEmployeeOnboarding: this.isEmployeeOnboarding,
                            selfServe,
                            KYCKYB,
                            hasKYCExpired: this.hasKYCExpired,
                            hasKYBExpired,
                            kycKybWave: this.authService.company.companyKycKybWave?.kycKybWave,
                            hasPayrollAdminContact,
                        };
                    })
                );
            })
        );
    }

    /**
     * Gets all observable values related to onboarding
     * @returns null or undefined if not applicable
     */
    private getOnboardingStatus(): Observable<undefined | SelfServeDisplayConditions> {
        // Only admins go through the self-serve quick start onboarding flow
        if (!this.isAdmin) {
            return of(undefined);
        }

        return this.onboardingStatusService.onboardingStatus$.asObservable().pipe(
            map((onboardingStatus) => ({
                onboardingStatus,
                selfServeQuickstart: this.authService.company.selfServeQuickstart,
                isSelfServe: this.authService.company.isSelfServe,
            }))
        );
    }

    /**
     * Gets all observable values related to Know-Your-Business and Know-Your-Client
     * @returns null or undefined if not applicable
     */
    private getKYCKYBStatus(): Observable<undefined | KYCKYBDisplayConditions> {
        // KYB and KYC are only applicable for users that have access to the payroll module
        if (!this.hasPayroll && !this.hasKYCExpired) {
            return of(undefined);
        }

        // All users with access to payroll need to do KYC
        const personaObservables: Observable<HumiInquiryStatus>[] = [];

        personaObservables.push(
            // Completes the observable when the status is no longer in an incomplete status (we don't need to show the banner any longer)
            this.knowYourService.accountStatus.pipe(
                takeWhile((status) => this.knowYourService.isIncompleteState(status), true)
            )
        );

        // Only payroll admins need to do KYB
        if (this.isAdmin) {
            personaObservables.push(
                this.knowYourService.companyStatus.pipe(
                    takeWhile((status) => this.knowYourService.isIncompleteState(status), true)
                )
            );
        }

        const hasWave = this.authService.company.hasWaveDefinition;
        const enabled$ = hasWave ? of(true) : from(this.featureService.has(FeatureFlag.KYC_KYB));

        return enabled$.pipe(
            switchMap((isKycKybEnabled) => {
                if (!isKycKybEnabled) {
                    return of(undefined);
                }
                return combineLatest(personaObservables).pipe(
                    map(([KYCStatus, KYBStatus]) => ({
                        hasVerifiedPersonaIdentity: !this.knowYourService.isIncompleteState(KYCStatus),
                        hasVerifiedPersonaBusiness: !this.knowYourService.isIncompleteState(KYBStatus),
                        kybStatus: KYBStatus,
                    }))
                );
            })
        );
    }

    private getCurrentURL(): Observable<string> {
        return this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event: NavigationEnd) => event.urlAfterRedirects)
        );
    }

    /**
     * Refresh payroll admin contact status
     * Call this when contacts are added/removed to update the banner visibility
     */
    async refreshPayrollAdminContactStatus(): Promise<void> {
        if (!this.hasPayroll) {
            this.payrollAdminContactStatus$.next(true);
            return;
        }

        try {
            const [contacts] = await CompanyAdminContact.param('company', this.authService.company.id).limit(1).get();
            this.payrollAdminContactStatus$.next(contacts.length > 0);
        } catch (error) {
            console.error(
                this.translateService.instant('payroll.contactSetup.errorCheckingPayrollAdminContacts'),
                error
            );
            this.payrollAdminContactStatus$.next(true);
        }
    }

    //Checks if the company has at least one payroll admin contact
    private getPayrollAdminContactStatus(): Observable<boolean> {
        return this.payrollAdminContactStatus$.asObservable();
    }

    /**
     * Checks if company persona is expiring or expired (within 30 days of expiration)
     * Used for both KYC and KYB re-verification banners
     * Requires KYC_KYB_RE_VERIFICATION feature flag to be enabled
     */
    private getHasKYBExpiredStatus(): Observable<boolean> {
        return from(this.featureService.has(FeatureFlag.KYC_KYB_RE_VERIFICATION)).pipe(
            map((isFlagEnabled) => {
                if (!isFlagEnabled) {
                    return false;
                }
                return this.hasKYBExpired;
            })
        );
    }
}
